import { isAxiosError } from "axios";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { SkillEditor } from "@/components/features/skills/SkillEditor";
import { skillDetailQueryOptions } from "@/lib/data/react-query/skills";
import { Button } from "@/components/ui/button";
import {
  libraryContentQueryOptions,
  libraryQueryKey,
} from "@/lib/data/react-query/file-library";
import { saveLibraryContent } from "@/lib/data/requests/file-library";
import { automationError } from "@/components/features/automations/automationError";
export function LibraryContentEditor({
  id,
  onClose,
  skillId,
}: {
  skillId?: string;
  id: string;
  onClose: () => void;
}) {
  const client = useQueryClient();
  const closeFriendly = () => {
    void client.invalidateQueries({ queryKey: libraryQueryKey });
    onClose();
  };
  const [mode, setMode] = useState<"file" | "editor">("file");
  const skill = useQuery({
    ...skillDetailQueryOptions(skillId ?? ""),
    enabled: !!skillId && mode === "editor",
    refetchOnWindowFocus: false,
  });
  const content = useQuery({
    ...libraryContentQueryOptions(id),
    refetchOnWindowFocus: false,
  });
  return (
    <>
      <div className="mb-4 flex gap-2">
        <Button
          variant={mode === "file" ? "default" : "outline"}
          onClick={() => {
            setMode("file");
          }}
        >
          SKILL.md
        </Button>
        {skillId && (
          <Button
            variant={mode === "editor" ? "default" : "outline"}
            onClick={() => {
              setMode("editor");
            }}
          >
            Skill editor
          </Button>
        )}
      </div>
      <div hidden={mode !== "editor"}>
        {skill.data ? (
          <SkillEditor
            skill={skill.data}
            initialScope={skill.data.scope}
            scopes={[skill.data.scope]}
            onClose={closeFriendly}
          />
        ) : (
          <p>{skill.error ? automationError(skill.error) : "Loading skill…"}</p>
        )}
      </div>
      <div hidden={mode !== "file"}>
        {content.data ? (
          <Editor
            key={`${id}:${content.data.revision}`}
            id={id}
            content={content.data.canonicalContent}
            revision={content.data.revision}
            onClose={onClose}
          />
        ) : (
          <p role={content.error ? "alert" : "status"}>
            {content.error ? automationError(content.error) : "Loading skill…"}
          </p>
        )}
      </div>
    </>
  );
}
function Editor({
  id,
  content,
  revision,
  onClose,
}: {
  id: string;
  content: string;
  revision: string;
  onClose: () => void;
}) {
  const [value, setValue] = useState(content);
  const client = useQueryClient();
  const save = useMutation({
    mutationFn: () => saveLibraryContent(id, revision, value),
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: libraryQueryKey }),
        client.invalidateQueries({ queryKey: ["skills"] }),
      ]);
      onClose();
    },
  });
  return (
    <div className="space-y-4">
      <textarea
        aria-label="SKILL.md"
        className="bg-background min-h-96 w-full rounded-md border p-3 font-mono text-sm"
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
        }}
      />
      {save.error && (
        <p role="alert" className="text-destructive">
          {isAxiosError(save.error) && save.error.response?.status === 409
            ? "Someone changed this skill while you were editing. Your changes have not been saved. Copy your changes, then reopen the file to review the latest version."
            : automationError(save.error)}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Button variant="outline" disabled={save.isPending} onClick={onClose}>
          Cancel
        </Button>
        <Button
          disabled={save.isPending}
          onClick={() => {
            save.mutate();
          }}
        >
          {save.isPending ? "Saving…" : "Save file"}
        </Button>
      </div>
    </div>
  );
}
